"use client";
import { useState, useCallback, useEffect, useRef } from "react";
import { useRouter, usePathname } from "next/navigation";
import useSearch from "@/app/lib/features/search/search.hook";
import { trackSearch } from "@/app/lib/tracking/events";
import { trackGeneric } from "@/app/lib/tracking/interactions";

const SEARCH_RESULTS_PATH = "/aramasonucu";

// useSearchParams Suspense gerektirir — bu hook'tan kaldırıldı.
// q param sync'i SearchParamsSync (ayrı Suspense boundary) tarafından yapılır.
const useSearchState = () => {
  const router = useRouter();
  const pathname = usePathname();
  const [value, setValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { saveSearch } = useSearch();

  // Search input Header'ın parçası ve sayfa geçişlerinde unmount olmuyor;
  // "value" state'i route değişse bile kalıcı kalıyordu (arama sonuçları
  // sayfasından anasayfaya dönünce yazdığın kelime kutuda kalmaya devam
  // ediyordu). Sonuç sayfasından ÇIKARKEN kutuyu temizle.
  const prevPathnameRef = useRef(pathname);
  useEffect(() => {
    const prevPathname = prevPathnameRef.current;
    prevPathnameRef.current = pathname;
    if (prevPathname === SEARCH_RESULTS_PATH && pathname !== SEARCH_RESULTS_PATH) {
      setValue("");
    }
  }, [pathname]);

  // q param sync dışarıdan (SearchParamsSync) yapılır
  const syncFromParams = useCallback((q: string) => {
    setValue(q);
  }, []);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  const handleSetValue = useCallback((val: string) => {
    setValue(val);
    if (val) setIsOpen(true);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: PointerEvent) => {
      if (window.innerWidth < 768) return;
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        close();
      }
    };

    // pointerdown + capture phase: Swiper (Stories, ürün carousel'leri vb.),
    // hedef eleman (ör. buton içindeki <img>) kendi "focusable elements"
    // listesinde değilse pointerdown'da preventDefault çağırıyor — bu da
    // tarayıcının o etkileşim için ürettiği mousedown event'ini tamamen
    // iptal ediyor (mousedown hiç doğmuyor). pointerdown bundan etkilenmez.
    document.addEventListener("pointerdown", handleClickOutside, true);
    return () =>
      document.removeEventListener("pointerdown", handleClickOutside, true);
  }, [isOpen, close]);

  // Sayfa değişiminde (bir kart/link tıklayıp başka sayfaya geçince) header
  // unmount olmadığı için isOpen state'i kalıcı kalır — route değişince kapat.
  useEffect(() => {
    close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const submit = useCallback(
    async (term: string) => {
      const trimmed = term.trim();
      if (!trimmed) return;
      trackSearch(trimmed);
      trackGeneric("search_bar");
      await saveSearch({ search_term: trimmed });
      close();
      router.push(`/aramasonucu?q=${encodeURIComponent(trimmed)}`);
    },
    [saveSearch, close, router],
  );

  return { value, setValue: handleSetValue, syncFromParams, isOpen, open, close, submit, containerRef };
};

export default useSearchState;
